import * as easing from 'svelte/easing';
import { fly } from 'svelte/transition';
import { isOnScreen, union } from './geometry';
export var ListElementState;
(function (ListElementState) {
    ListElementState[ListElementState["Normal"] = 0] = "Normal";
    ListElementState[ListElementState["Outro"] = 1] = "Outro";
    ListElementState[ListElementState["PendingDeletion"] = 2] = "PendingDeletion";
})(ListElementState || (ListElementState = {}));
/// Chains two animations together so that the execute in sequence
export function chain(animation1, animation2) {
    const animation1fraction = animation1.duration / (animation1.duration + animation2.duration);
    return {
        delay: 0,
        duration: animation1.duration + animation2.duration,
        easing: easing.linear,
        keyframeHints: [animation1fraction - 0.001, animation1fraction + 0.001],
        css: (t, u) => {
            if (t < animation1fraction) {
                t /= animation1fraction;
                return animation1.css(t, 1 - t);
            }
            else {
                t -= animation1fraction;
                t /= 1 - animation1fraction;
                return animation2.css(t, 1 - t);
            }
        },
    };
}
export function fixPosition(node, currentPosition, desiredPosition) {
    const style = getComputedStyle(node);
    if (style.position !== "absolute" && style.position !== "fixed") {
        return {
            static_css: `position: absolute; width: ${desiredPosition.width}px; height: ${desiredPosition.height}px;`,
            transform: `translate(${desiredPosition.left - currentPosition.left}px, ${desiredPosition.top - currentPosition.top}px)`,
        };
    }
    else {
        return {
            static_css: "",
            transform: "",
        };
    }
}
export function itemTransitionOut(node, location) {
    // This is essentially a fly animation with one small tweak.
    // Instead of getting the current transform from getComputedStyle
    // we get it from `node.style.transform`. This resolves an issue
    // in which an element would show up in the wrong position
    // when the out transition started while the item was currently animating.
    // I think this is because svelte tries to pin the node to an absolute position
    // but the current animation overrides that (maybe it hasn't been fully stopped yet)
    // and so getComputedStyle returns the wrong values.
    const opacity = 0;
    const style = getComputedStyle(node);
    const fromZ = parseInt(style.zIndex);
    // Convert opacity string to a number
    const currentOpacity = +style.opacity;
    const transform = node.style.transform;
    let desiredPosition = node.getBoundingClientRect();
    const y = 200;
    const opacityDelta = opacity - currentOpacity;
    return () => {
        // We will make the element use absolute positioning. So its coordinate system will be relative to the parent.
        let actualPosition = (node.parentElement || document.body).getBoundingClientRect();
        // Don't bother animating off-screen items
        if (!isOnScreen(desiredPosition)) {
            return null;
        }
        const { static_css, transform } = fixPosition(node, actualPosition, desiredPosition);
        return {
            delay: 0,
            duration: 500,
            easing: easing.quadOut,
            static_css: `${static_css}z-index: ${fromZ != 0 ? fromZ : -1};`,
            css: (t, u) => `
                transform: ${transform} translate(0px, ${t * y}px);
                opacity: ${currentOpacity + t * opacityDelta}`,
        };
    };
}
export function itemTransitionIn(node, location) {
    // Don't bother animating off-screen items
    if (!isOnScreen(node.getBoundingClientRect())) {
        return null;
    }
    let config = fly(node, { duration: 500, delay: 0, y: 200, easing: easing.quadOut });
    const style = getComputedStyle(node);
    const fromZ = parseInt(style.zIndex);
    return Object.assign(Object.assign({}, config), { static_css: `z-index: ${fromZ != 0 ? fromZ : 1};`, css: config.css || (() => ""), duration: config.duration || 0 });
}
export function itemTransition(node, location, swapsPlaces) {
    const from = node.getBoundingClientRect();
    const style = getComputedStyle(node);
    const fromOpacity = parseFloat(style.opacity);
    const fromZ = style.zIndex === "auto" ? 0 : parseInt(style.zIndex);
    return () => {
        const to = node.getBoundingClientRect();
        // Don't bother animating off-screen items
        if (!isOnScreen(union(from, to))) {
            return null;
        }
        const dx = from.left - to.left;
        let dy = from.top - to.top;
        const delay = 0;
        const duration = (d) => Math.sqrt(d) * 180;
        const easing$1 = easing.linear;
        let desiredZ = dy > 0 ? 1 : dx < 0 ? 1 : -1;
        if (fromZ != 0)
            desiredZ = fromZ;
        let targetOpacity = 1;
        let durationValue = duration(Math.sqrt(dx * dx + dy * dy));
        let opacityFn = (t) => targetOpacity * t + (1 - t) * fromOpacity;
        let x_offset_fn = (t) => 0;
        let fromTransform = `translate(${dx}px,${dy}px)`;
        const animationDurationMultiplier = 1;
        // If the item moves very far then split the animation in two parts.
        // First fade the item out, and then fade it in at the new position.
        if (Math.abs(dy) > 1200 && swapsPlaces) {
            const dir = Math.sign(dy);
            return chain({
                duration: 400 * animationDurationMultiplier,
                css: (t, u) => {
                    // Svelte trick: see below
                    // t = Math.min(1, t * 1.1);
                    t = easing.quadIn(t);
                    return `transform: ${fromTransform} translate(0px, ${-dir * 200 * t}px); opacity: ${1 - t}; z-index: ${desiredZ};`;
                },
            }, {
                duration: 400 * animationDurationMultiplier,
                css: (t, u) => {
                    // Svelte trick: see below
                    // t = Math.min(1, t * 1.1);
                    t = easing.quadOut(t);
                    return `transform: translate(0px, ${dir * 200 * (1 - t)}px); opacity: ${t}; z-index: ${desiredZ};`;
                },
            });
        }
        else {
            if (swapsPlaces && Math.abs(dy) > 50) {
                // Move the item along a curve instead of along a straight line.
                x_offset_fn = (t) => {
                    return (1 - Math.pow(Math.abs(t - 0.5) * 2, 4)) * Math.sign(dy) * Math.sqrt(Math.abs(dy)) * 1;
                };
                // If the item moves far then fade it out in the middle of the swap
                if (Math.abs(dy) > 500) {
                    let prevOpacityFn = opacityFn;
                    opacityFn = (t) => prevOpacityFn(t) * Math.pow(Math.abs(t - 0.5) * 2, 2);
                    durationValue = 750;
                }
            }
            durationValue = Math.min(durationValue, 750);
            durationValue *= animationDurationMultiplier;
            return {
                delay,
                duration: durationValue,
                easing: easing$1,
                static_css: `z-index: ${fromZ != 0 ? fromZ : dy > 0 ? 1 : -1};`,
                css: (t, u) => {
                    // We trick svelte that the animation is longer than it actually is
                    // to avoid svelte removing the animation a few frames before it is actually done.
                    // This significantly reduces jank at the end of the animation.
                    // t = Math.min(1, t * 1.1);
                    t = easing.cubicInOut(t);
                    u = 1 - t;
                    const x = u * dx + x_offset_fn(t);
                    const y = u * dy;
                    const sx = t + (u * from.width) / to.width;
                    const sy = t + (u * from.height) / to.height;
                    const opacity = opacityFn(t);
                    return `transform: translate(${x}px, ${y}px); opacity: ${opacity}; z-index: ${desiredZ};`;
                },
            };
        }
    };
}
